import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { GeoPlace } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { useAction } from "./ui.tsx";

export interface LatLon {
  latitude: number;
  longitude: number;
}

// Without a position the map shows central Europe; a found or clicked place is shown at street level.
const OVERVIEW: L.LatLngTuple = [51.5, 15];
const OVERVIEW_ZOOM = 4;
const PLACE_ZOOM = 16;

const pin = L.divIcon({
  className: "map-pin",
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

/**
 * Where a place is: search an address, or click or drag on the map. Nobody has to know latitude and longitude;
 * they are shown only for reference. Tiles come from OpenStreetMap, addresses through the platform's geocoder.
 */
export function LocationPicker(props: {
  value: LatLon | null;
  onChange: (value: LatLon | null) => void;
}) {
  let host!: HTMLDivElement;
  let map: L.Map | undefined;
  let marker: L.Marker | undefined;
  const [query, setQuery] = createSignal("");
  const [results, setResults] = createSignal<GeoPlace[]>();
  const [run, error, busy] = useAction();

  const place = (value: LatLon | null, zoom?: number) => {
    props.onChange(value);
    if (!map) {
      return;
    }
    if (value === null) {
      marker?.remove();
      marker = undefined;
      return;
    }
    const at: L.LatLngTuple = [value.latitude, value.longitude];
    if (marker) {
      marker.setLatLng(at);
    } else {
      marker = L.marker(at, {
        icon: pin,
        draggable: true,
        keyboard: true,
        title: t("assets.location"),
      }).addTo(map);
      marker.on("dragend", () => {
        const p = marker!.getLatLng();
        props.onChange(round({ latitude: p.lat, longitude: p.lng }));
      });
    }
    if (zoom !== undefined) {
      map.setView(at, zoom);
    }
  };

  onMount(() => {
    map = L.map(host, { zoomControl: true, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      className: "street-tiles",
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
    }).addTo(map);
    if (props.value) {
      map.setView([props.value.latitude, props.value.longitude], PLACE_ZOOM);
      place(props.value);
    } else {
      map.setView(OVERVIEW, OVERVIEW_ZOOM);
    }
    map.on("click", (e: L.LeafletMouseEvent) =>
      place(round({ latitude: e.latlng.lat, longitude: e.latlng.lng })),
    );
    // The dialog lays out after mounting; Leaflet measures its box once it is visible.
    const resize = new ResizeObserver(() => map?.invalidateSize());
    resize.observe(host);
    onCleanup(() => {
      resize.disconnect();
      map?.remove();
    });
  });

  // Enter searches instead of submitting the surrounding form.
  const search = () =>
    void run(async () => {
      const found = await api.geo.search(query().trim(), locale());
      setResults(found);
      if (found.length === 1) {
        choose(found[0]!);
      }
    });
  const choose = (p: GeoPlace) => {
    place(round(p), PLACE_ZOOM);
    setResults(undefined);
  };

  return (
    <div class="stack" style={{ gap: "8px" }}>
      <div class="row-acts">
        <input
          id="placeSearch"
          type="search"
          style={{ flex: "1" }}
          placeholder={t("assets.search")}
          aria-label={t("assets.search")}
          maxLength={200}
          value={query()}
          onInput={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              search();
            }
          }}
        />
        <button
          type="button"
          class="btn"
          disabled={busy() || query().trim().length < 3}
          onClick={search}
        >
          <Icon name="search" />
          {t("assets.searchButton")}
        </button>
      </div>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
      <Show when={results()}>
        {(list) => (
          <Show
            when={list().length > 0}
            fallback={
              <p class="hint" style={{ margin: 0 }}>
                {t("assets.searchNone")}
              </p>
            }
          >
            <ul class="geo-results">
              <For each={list()}>
                {(p) => (
                  <li>
                    <button type="button" onClick={() => choose(p)}>
                      {p.label}
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        )}
      </Show>
      <div ref={host} class="map" />
      <div class="row-acts">
        <span class="hint" style={{ flex: "1" }}>
          {props.value ? t("assets.mapHint") : t("assets.noLocation")}
        </span>
        <Show when={props.value}>
          {(v) => (
            <>
              <span class="mono hint">
                {v().latitude.toFixed(5)}, {v().longitude.toFixed(5)}
              </span>
              <button
                type="button"
                class="btn sm ghost"
                onClick={() => place(null)}
              >
                {t("assets.clearLocation")}
              </button>
            </>
          )}
        </Show>
      </div>
    </div>
  );
}

// About 1 m: more digits only suggest a precision no address or click has.
function round(p: LatLon): LatLon {
  return {
    latitude: Math.round(p.latitude * 1e5) / 1e5,
    longitude: Math.round(p.longitude * 1e5) / 1e5,
  };
}
